import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ToastContainer } from 'react-toastify';
import { HelmetProvider } from 'react-helmet-async';
import 'react-toastify/dist/ReactToastify.css';
import Navigation from './components/Navigation';
import Footer from './components/Footer';
import HomePage from './pages/HomePage';
import Login from './pages/Login';
import PostList from './pages/PostList';
import PostEditor from './components/PostEditor';
import PostView from './pages/PostView';
import TagView from './pages/TagView';
import TagList from './pages/admin/TagList';
import TagForm from './pages/admin/TagForm';
import Sobre from './pages/Sobre';
import Documentacao from './pages/Documentacao';
import Contato from './pages/Contato';
import NotFound from './pages/NotFound';
import './styles/globals.css';
import './HomePage.css';

// Componente para rotas protegidas
function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <div className="flex items-center justify-center min-h-[200px] text-muted-foreground">Carregando...</div>;
  }

  return isAuthenticated ? children : <Navigate to="/login" />;
}


// Componente de Layout principal
function Layout({ children, hideNavigation = false }) {
  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      {!hideNavigation && <Navigation />}
      <main className="flex-1 w-full">
        {children}
      </main>
      <Footer />
    </div>
  );
}

// Componente de Layout para páginas de leitura
function ReadingLayout({ children }) {
  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      {/* Reading layout might also need navigation, usually */}
      <Navigation />
      <main className="flex-1 w-full">
        {children}
      </main>
      <Footer />
    </div>
  );
}


function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <AuthProvider>
          <Router>
            <div className="App">
              <ToastContainer
                position="top-center"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="colored"
              />
              <Routes>
                {/* Rota principal */}
                <Route path="/" element={
                  <Layout>
                    <HomePage />
                  </Layout>
                } />

                {/* Login Route - Admin Only */}
                <Route path="/admin" element={<Login />} />
                <Route path="/login" element={<Navigate to="/admin" />} />

                {/* Rotas protegidas para autores e admins */}
                <Route path="/admin/posts" element={
                  <ProtectedRoute>
                    <Layout>
                      <PostList />
                    </Layout>
                  </ProtectedRoute>
                } />
                <Route path="/admin/posts/new" element={
                  <ProtectedRoute>
                    <Layout>
                      <PostEditor mode="create" />
                    </Layout>
                  </ProtectedRoute>
                } />
                <Route path="/admin/posts/edit/:id" element={
                  <ProtectedRoute>
                    <Layout>
                      <PostEditor mode="edit" />
                    </Layout>
                  </ProtectedRoute>
                } />

                {/* Rota pública para visualização de posts */}
                <Route path="/posts/:slug" element={
                  <ReadingLayout>
                    <PostView />
                  </ReadingLayout>
                } />

                {/* Rotas para gerenciamento de tags (admin) */}
                <Route path="/admin/tags" element={
                  <ProtectedRoute>
                    <Layout>
                      <TagList />
                    </Layout>
                  </ProtectedRoute>
                } />
                <Route path="/admin/tags/new" element={
                  <ProtectedRoute>
                    <Layout>
                      <TagForm />
                    </Layout>
                  </ProtectedRoute>
                } />
                <Route path="/admin/tags/edit/:id" element={
                  <ProtectedRoute>
                    <Layout>
                      <TagForm />
                    </Layout>
                  </ProtectedRoute>
                } />

                {/* Rota para visualização de posts por tag */}
                <Route path="/tag/:slug" element={
                  <Layout>
                    <TagView />
                  </Layout>
                } />

                {/* Páginas públicas do footer */}
                <Route path="/sobre" element={
                  <Layout>
                    <Sobre />
                  </Layout>
                } />
                <Route path="/documentacao" element={
                  <Layout>
                    <Documentacao />
                  </Layout>
                } />
                <Route path="/contato" element={
                  <Layout>
                    <Contato />
                  </Layout>
                } />

                {/* Rota de fallback para URLs não encontradas */}
                <Route path="*" element={
                  <Layout>
                    <NotFound />
                  </Layout>
                } />
              </Routes>
            </div>
          </Router>
        </AuthProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;

